import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { loadError } from "@/lib/api";
import { euro, fmtDate, nights, PAYMENT_METHODS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Plus, Trash2, Printer, Download, Mail, Undo2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const DOC_LABEL = { fattura: "Fattura", ricevuta: "Ricevuta", nota_credito: "Nota di credito" };
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [structure, setStructure] = useState({ structure_name: "", structure_vat: "", structure_address: "" });
  const [logoUrl, setLogoUrl] = useState("");
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLang, setPreviewLang] = useState("it");
  const [form, setForm] = useState(null);
  const T = previewLang === "en"
    ? { fattura: "Invoice", ricevuta: "Receipt", notaCredito: "Credit note", vat: "VAT", holder: "Billed to", desc: "Description", amount: "Amount", stay: "Stay", nightsW: "nights", tax: "Tourist tax", total: "Total", print: "Print / PDF", n: "No.", issuedBy: "Issued by", send: "Send by email", sentTo: "Sent to" }
    : { fattura: "Fattura", ricevuta: "Ricevuta", notaCredito: "Nota di credito", vat: "P.IVA", holder: "Intestatario", desc: "Descrizione", amount: "Importo", stay: "Soggiorno", nightsW: "notti", tax: "Imposta di soggiorno", total: "Totale", print: "Stampa / PDF", n: "n.", issuedBy: "Emesso da", send: "Invia via email", sentTo: "Inviata a" };

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data)).catch(loadError);
    api.get("/bookings").then((r) => setBookings(r.data)).catch(loadError);
    api.get("/settings").then((r) => { setStructure({ structure_name: r.data.structure_name, structure_vat: r.data.structure_vat, structure_address: r.data.structure_address }); setLogoUrl(r.data.logo_url || ""); }).catch(loadError);
  };
  useEffect(() => { load(); }, []);

  const [params, setParams] = useSearchParams();
  const wantedBooking = params.get("booking");
  useEffect(() => {
    if (!wantedBooking || !bookings.length || !structure.structure_name) return;
    const existing = invoices.find((i) => i.booking_id === wantedBooking);
    if (existing) { setPreviewLang("it"); setPreview(existing); }
    else {
      openNew();
      onPickBooking(wantedBooking);
    }
    setParams({}, { replace: true });
    // eslint-disable-next-line
  }, [wantedBooking, bookings, invoices, structure]);

  const openNew = () => {
    setForm({
      booking_id: "", doc_type: "ricevuta", ...structure,
      guest_name: "", guest_fiscal: "", nights: 1, rate: 0, tourist_tax: 0,
      payment_method: "Contanti", notes: "",
    });
    setOpen(true);
  };

  const onPickBooking = (id) => {
    const b = bookings.find((x) => x.id === id);
    if (!b) { setForm((f) => ({ ...f, booking_id: id })); return; }
    const n = nights(b.check_in, b.check_out) || 1;
    setForm((f) => ({
      ...f, booking_id: id, guest_name: b.guest_name, guest_fiscal: b.codice_fiscale || "",
      nights: n, rate: Math.round((b.total_amount / n) * 100) / 100,
      tourist_tax: b.num_guests * n * 2,
    }));
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const openSend = () => {
    const b = bookings.find((x) => x.id === preview?.booking_id);
    setSendTo(preview?.sent_to || b?.guest_email || "");
    setSendOpen(true);
  };
  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${preview.id}/send-email`, { to: sendTo, lang: previewLang });
      toast.success(`Documento inviato a ${data.sent_to}`);
      setPreview((p) => ({ ...p, sent_to: data.sent_to, sent_at: data.sent_at }));
      setSendOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setSending(false); }
  };

  const save = async () => {
    if (!form.guest_name || !form.rate) { toast.error("Ospite e tariffa obbligatori"); return; }
    try {
      const payload = { ...form, nights: Number(form.nights), rate: Number(form.rate), tourist_tax: Number(form.tourist_tax) };
      const { data } = await api.post("/invoices", payload);
      toast.success(`Documento ${data.number} creato`);
      setOpen(false); load(); setPreview(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const creditNote = async (inv) => {
    if (!window.confirm(`Stornare il documento ${inv.number} con una nota di credito?`)) return;
    try {
      const { data } = await api.post(`/invoices/${inv.id}/credit-note`);
      toast.success(`Nota di credito ${data.number} emessa`);
      load(); setPreviewLang("it"); setPreview(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Eliminare il documento ${inv.number}?`)) return;
    await api.delete(`/invoices/${inv.id}`);
    load();
  };

  const downloadPdf = async (inv, lang = "it") => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf?lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.doc_type}_${inv.number.replace("/", "-")}_${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`PDF ${lang.toUpperCase()} scaricato`);
    } catch (err) {
      toast.error("Download non riuscito");
    }
  };

  const subtotal = form ? Number(form.nights || 0) * Number(form.rate || 0) : 0;
  const total = subtotal + Number(form?.tourist_tax || 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Amministrazione</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Fatture & Ricevute</h1>
        </div>
        <Button data-testid="new-invoice-button" onClick={openNew} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11 px-5 font-heading">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Documento
        </Button>
      </div>

      <div className="grid gap-4" data-testid="invoices-list">
        {invoices.map((inv) => (
          <Card key={inv.id} className="p-5 border-border shadow-sm flex flex-wrap items-center justify-between gap-4 animate-fade-up">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-secondary grid place-items-center"><FileText className="w-5 h-5 text-accent" /></div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-heading font-semibold">{DOC_LABEL[inv.doc_type] || "Ricevuta"} n. {inv.number}</span>
                  {inv.reversed_by && <Badge data-testid={`reversed-badge-${inv.id}`} className="bg-red-50 text-red-700 border-0">Stornata da {inv.reversed_by_number}</Badge>}
                  {inv.doc_type === "nota_credito" && <Badge data-testid={`credit-note-badge-${inv.id}`} className="bg-stone-100 text-stone-700 border-0">Storno di {inv.refers_to_number}</Badge>}
                </div>
                <p className="text-sm text-muted-foreground">{inv.guest_name} · {fmtDate(inv.created_at)} · <span data-testid={`issued-by-${inv.id}`}>emessa da {inv.issued_by || "—"}</span>
                  {inv.sent_to && <span data-testid={`sent-to-${inv.id}`} className="text-emerald-700"> · inviata a {inv.sent_to} il {fmtDate(inv.sent_at)}</span>}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-heading text-lg font-semibold">{euro(inv.total)}</span>
              <Button variant="outline" size="sm" data-testid={`pdf-invoice-${inv.id}`} onClick={() => downloadPdf(inv, "it")}><Download className="w-4 h-4 mr-1.5" />PDF IT</Button>
              <Button variant="outline" size="sm" data-testid={`pdf-invoice-en-${inv.id}`} onClick={() => downloadPdf(inv, "en")}><Download className="w-4 h-4 mr-1.5" />PDF EN</Button>
              <Button variant="outline" size="sm" data-testid={`view-invoice-${inv.id}`} onClick={() => { setPreviewLang("it"); setPreview(inv); }}><Printer className="w-4 h-4 mr-1.5" />Anteprima</Button>
              <Button variant="outline" size="sm" data-testid={`view-invoice-en-${inv.id}`} onClick={() => { setPreviewLang("en"); setPreview(inv); }}><Printer className="w-4 h-4 mr-1.5" />Preview EN</Button>
              {isAdmin && inv.doc_type !== "nota_credito" && !inv.reversed_by && <Button variant="outline" size="sm" data-testid={`credit-note-${inv.id}`} onClick={() => creditNote(inv)} className="text-red-700 border-red-200 hover:bg-red-50"><Undo2 className="w-4 h-4 mr-1.5" />Storna</Button>}
              {isAdmin && <Button variant="ghost" size="sm" data-testid={`delete-invoice-${inv.id}`} onClick={() => remove(inv)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>}
            </div>
          </Card>
        ))}
        {invoices.length === 0 && <Card className="p-10 text-center text-muted-foreground border-dashed">Nessun documento emesso. Crea la prima ricevuta.</Card>}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Nuovo documento</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Tipo</Label>
                  <Select value={form.doc_type} onValueChange={(v) => set("doc_type", v)}>
                    <SelectTrigger data-testid="invoice-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ricevuta">Ricevuta</SelectItem>
                      <SelectItem value="fattura">Fattura</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Da prenotazione</Label>
                  <Select value={form.booking_id} onValueChange={onPickBooking}>
                    <SelectTrigger data-testid="invoice-booking" className="mt-1.5"><SelectValue placeholder="Opzionale" /></SelectTrigger>
                    <SelectContent>
                      {bookings.map((b) => <SelectItem key={b.id} value={b.id}>{b.guest_name} — {b.room_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider">Ospite</Label><Input data-testid="invoice-guest" className="mt-1.5" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider">Cod. Fiscale / P.IVA</Label><Input className="mt-1.5" value={form.guest_fiscal} onChange={(e) => set("guest_fiscal", e.target.value)} /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-xs uppercase tracking-wider">Notti</Label><Input type="number" className="mt-1.5" value={form.nights} onChange={(e) => set("nights", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider">Tariffa/notte</Label><Input type="number" data-testid="invoice-rate" className="mt-1.5" value={form.rate} onChange={(e) => set("rate", e.target.value)} /></div>
                <div><Label className="text-xs uppercase tracking-wider">Imposta soggiorno</Label><Input type="number" className="mt-1.5" value={form.tourist_tax} onChange={(e) => set("tourist_tax", e.target.value)} /></div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Metodo di pagamento</Label>
                <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs uppercase tracking-wider">Note</Label><Textarea className="mt-1.5" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
              <div className="bg-secondary/50 rounded-lg p-3 flex justify-between font-heading">
                <span>Imponibile {euro(subtotal)} + Imposta {euro(Number(form.tourist_tax || 0))}</span>
                <span className="font-semibold">Totale {euro(total)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-invoice-button" onClick={save} className="bg-accent hover:bg-accent/90 text-accent-foreground">Emetti documento</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="invoice-preview">
          {preview && (
            <div className="space-y-5" id="invoice-print" data-testid={`invoice-preview-${previewLang}`}>
              <div className="flex justify-end gap-1 print:hidden">
                {["it", "en"].map((l) => (
                  <button key={l} data-testid={`preview-lang-${l}`} onClick={() => setPreviewLang(l)}
                    className={`text-xs px-3 py-1 rounded-full border transition-colors ${previewLang === l ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:bg-secondary"}`}>
                    {l.toUpperCase()}
                  </button>
                ))}
              </div>
              <div className="flex items-start justify-between border-b border-border pb-4">
                <div className="flex items-start gap-3">
                  {logoUrl && <img src={logoUrl} alt="" data-testid="preview-logo" className="w-12 h-12 object-contain rounded-lg" />}
                  <div>
                    <div className="font-display text-2xl">{preview.structure_name}</div>
                    <p className="text-xs text-muted-foreground">{preview.structure_address}</p>
                    <p className="text-xs text-muted-foreground">{T.vat} {preview.structure_vat}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground" data-testid="preview-doc-type">{preview.doc_type === "fattura" ? T.fattura : preview.doc_type === "nota_credito" ? T.notaCredito : T.ricevuta}</p>
                  <p className="font-heading font-semibold">{T.n} {preview.number}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(preview.created_at)}</p>
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{T.holder}</p>
                <p className="font-medium">{preview.guest_name}</p>
                {preview.guest_fiscal && <p className="text-sm text-muted-foreground">{preview.guest_fiscal}</p>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2 font-medium">{T.desc}</th><th className="py-2 text-right font-medium">{T.amount}</th></tr></thead>
                <tbody>
                  <tr className="border-b border-border/60"><td className="py-2">{T.stay} {preview.nights} {T.nightsW} × {euro(preview.rate)}</td><td className="py-2 text-right">{euro(preview.subtotal)}</td></tr>
                  {preview.tourist_tax !== 0 && <tr className="border-b border-border/60"><td className="py-2">{T.tax}</td><td className="py-2 text-right">{euro(preview.tourist_tax)}</td></tr>}
                </tbody>
                <tfoot><tr><td className="py-3 font-heading font-semibold">{T.total} ({preview.payment_method})</td><td className="py-3 text-right font-heading font-semibold text-lg">{euro(preview.total)}</td></tr></tfoot>
              </table>
              {preview.notes && <p className="text-xs text-muted-foreground border-t border-border pt-3">{preview.notes}</p>}
              <p className="text-xs text-muted-foreground text-right" data-testid="preview-issued-by">{T.issuedBy}: {preview.issued_by || "—"}</p>
              <div className="flex gap-2">
                <Button data-testid="print-invoice-button" onClick={() => window.print()} className="flex-1 bg-primary text-primary-foreground"><Printer className="w-4 h-4 mr-2" />{T.print}</Button>
                <Button variant="outline" data-testid="preview-download-pdf" onClick={() => downloadPdf(preview, previewLang)}><Download className="w-4 h-4 mr-2" />PDF {previewLang.toUpperCase()}</Button>
                <Button variant="outline" data-testid="preview-send-email" onClick={openSend}><Mail className="w-4 h-4 mr-2" />{T.send}</Button>
              </div>
              {preview.sent_to && <p className="text-xs text-emerald-700 text-right" data-testid="preview-sent-info">{T.sentTo} {preview.sent_to} · {fmtDate(preview.sent_at)}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-sm" data-testid="send-invoice-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Invia documento via email</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Il PDF ({previewLang.toUpperCase()}) sarà allegato all'email.</p>
          <div>
            <Label className="text-xs uppercase tracking-wider">Destinatario</Label>
            <Input type="email" data-testid="send-invoice-to" className="mt-1.5" value={sendTo} onChange={(e) => setSendTo(e.target.value)} placeholder="ospite@email.it" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button data-testid="send-invoice-confirm" onClick={sendEmail} disabled={sending} className="bg-accent hover:bg-accent/90 text-accent-foreground"><Mail className="w-4 h-4 mr-2" />{sending ? "Invio…" : "Invia"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
